import { parseMoneyToCents } from '@agos/shared'

import { ruleIdFromSyntheticId, syntheticGiftId } from '../gift-id'
import type { OrderCartLine } from './cart'
import type { CartLine } from './cart-math'

/**
 * "Buy three pizzas, get a garlic bread."
 *
 * A gift the builder offers but never adds is a promotion the customer was
 * promised and did not get; one it adds twice is stock given away. Both are
 * counted here rather than in the JSX, and both are tested.
 *
 * Ported from legacy `pages/new-order.tsx`.
 */

/** Nobody has ever meant to give away more than this in one press. */
const MAX_GIFTS_PER_OFFER = 20

export interface ProductGiftRule {
  id: number
  active?: boolean
  /** How many matching items earn one gift. */
  minCount?: number
  /** Matched against a cart line's category name, case-insensitively. */
  categoryKeyword?: string
  giftName?: string
  giftPrice?: number | string
  /** Whether six matching items earn two gifts, or still only one. */
  multiply?: boolean
}

export interface GiftOffer {
  ruleId: number
  /** §9.697 - which table the rule is in: a count of products, or *Darčeky podľa sumy*. */
  ruleType: 'product' | 'amount'
  giftName: string
  giftPrice: number
  /** How many are still owed, after what is already in the cart. */
  count: number
}

export { ruleIdFromSyntheticId, syntheticGiftId }

/**
 * How many gifts from this rule the cart already holds.
 *
 * **§9.697 - by the rule's id and its type**, for a line that says which rule it is. This decoded the rule out of the
 * synthetic id alone, and the two rule tables share ids by accident: an amount gift on rule 3 counted as product rule
 * 3's gift and withheld it, and the reverse. The decode stays only for a line carrying no rule id - a cart built before
 * they were carried - and it only ever named a product rule.
 */
export function giftsInCartFor(
  cart: CartLine[],
  ruleId: number,
  ruleType: 'product' | 'amount' = 'product',
): number {
  return cart.reduce((count, line) => {
    if (line.giftRuleId !== undefined) {
      const type = line.giftRuleType === 'amount' ? 'amount' : 'product'
      return line.giftRuleId === ruleId && type === ruleType ? count + line.quantity : count
    }
    if (ruleType !== 'product' || line.productId >= 0) return count
    return ruleIdFromSyntheticId(line.productId) === ruleId ? count + line.quantity : count
  }, 0)
}

/**
 * Whether a line may count towards a gift rule at all - FB-804, §9.524.
 *
 * Two exclusions, each by the field that means it rather than by one id test standing in for both:
 *
 * - **A gift, or a gift earns a gift** and the offer never stops being made. `giftLinesFor` below always
 *   sets `giftRuleId`, which is what the payload carries too. `ruleIdFromSyntheticId` is deliberately
 *   *not* used here: a composed line's id is `pizzaCartId`'s hash, an arbitrary negative number that
 *   decodes to a perfectly plausible rule id, so excluding on it would throw every kombo away again -
 *   which is the defect this entry fixes, restored by its own fix.
 * - **A day's-menu dish**, which is legacy's `GIFT_THRESHOLD_EXCLUDED`. By `lunchMealType`, the field
 *   §10A.88 added so the kitchen can box the day's menu "without mistaking a custom pizza for one" -
 *   exactly this distinction, one screen over.
 *
 * **The lunch exclusion is load-bearing rather than tidy.** `NewOrderPage` gives a lunch line
 * `categoryName: section.label`, a label the franchise types itself, so without it a section named
 * *Jesenné menu* would start earning Nesvady's `jesenné menu` gift.
 */
function mayCount(line: OrderCartLine): boolean {
  return line.giftRuleId === undefined && line.lunchMealType === undefined
}

/**
 * How many items in the cart count towards a rule.
 *
 * **The line's own category decides, including for a line the catalogue has never heard of.** A kombo
 * and a custom pizza carry `categoryName: tt('Kombo pizza')` and `tt('Vlastná pizza')` from
 * `NewOrderPage`, so they count towards a rule keyed on `pizza` and not towards one keyed on `burger`.
 *
 * **FB-804 - this read `line.productId > 0` and threw them away before their category was ever
 * looked at.** Every composed line is negative by construction (`pizzaCartId`), so the owner's 3 EUR
 * gift pizza reached 62 of 103 web baskets whose only pizza was an assembled one and **0 of 14** phone
 * ones. The comment it replaces claimed those lines are excluded *as legacy does*, and legacy does the
 * opposite: `GIFT_VIRTUAL_COUNTING_TYPES = ["custom_pizza", "kombo_pizza"]` is tested *before* its
 * `id <= 0` exclusion, commented "Virtual types that DO count toward gift thresholds". Only the lunch
 * half of that sentence was true.
 *
 * Legacy's own branch is not ported: it counts a kombo **unconditionally, whatever the keyword**, which
 * is how a kombo would come to earn a gift on a rule about burgers. `giftOffers` in `@agos/customer-core`
 * asks this same question of a composed line's name, which is why the website was right all along.
 */
export function matchingQuantity(cart: OrderCartLine[], keyword: string): number {
  const needle = keyword.trim().toLowerCase()
  if (!needle) return 0
  return cart
    .filter(mayCount)
    .reduce(
      (total, line) =>
        (line.categoryName ?? '').toLowerCase().includes(needle) ? total + line.quantity : total,
      0,
    )
}

/** A rule the cart could earn but has not been given yet. */
export function pendingGiftOffers(cart: OrderCartLine[], rules: ProductGiftRule[]): GiftOffer[] {
  const offers: GiftOffer[] = []

  for (const rule of rules) {
    if (!rule.active) continue

    const minCount = rule.minCount
    if (!Number.isInteger(minCount) || (minCount ?? 0) < 1) continue

    const giftPrice = Number(rule.giftPrice)
    if (!Number.isFinite(giftPrice) || giftPrice < 0) continue

    const giftName = rule.giftName?.trim()
    if (!giftName) continue

    const keyword = rule.categoryKeyword?.trim().toLowerCase() ?? ''
    if (!keyword) continue

    const matched = matchingQuantity(cart, keyword)
    const earned = rule.multiply
      ? Math.floor(matched / (minCount ?? 1))
      : matched >= (minCount ?? 1)
        ? 1
        : 0

    const owed = Math.max(0, earned - giftsInCartFor(cart, rule.id))
    if (owed > 0) {
      offers.push({ ruleId: rule.id, ruleType: 'product', giftName, giftPrice, count: owed })
    }
  }

  return offers
}

/**
 * *Darčeky podľa sumy* at the till - `FRONTEND.md` §9.697, owner #21 / O75.
 *
 * **The owner**: *"darčeky by sa mohli dať robiť napríklad nad 70€ kofola zdarma"*. Decided (#124): offered once the
 * **food after discounts, without delivery**, reaches the rule's amount; the cashier accepts it; it is **taken back
 * when the order drops below**. Every reached rule is offered. The generated `GiftRule` is the shape `GET
 * /gifts/rules` answers, `minAmount` a string because `GiftController` answers the row untouched.
 */
export interface AmountGiftRule {
  id?: number
  giftItem?: string
  minAmount?: number | string
  active?: boolean
}

/** A rule's threshold in cents, or null for one that names no gift or no amount - which is never offered. */
function amountRuleOf(rule: AmountGiftRule): { id: number; giftName: string; minCents: number } | null {
  const giftName = rule.giftItem?.trim()
  if (rule.id === undefined || !giftName || rule.active === false) return null
  if (rule.minAmount === undefined || rule.minAmount === '') return null
  const minCents = parseMoneyToCents(rule.minAmount)
  if (!Number.isFinite(minCents) || minCents < 0) return null
  return { id: rule.id, giftName, minCents }
}

/** The amount gifts the order has reached and does not hold. One each, at nothing. */
export function pendingAmountGiftOffers(
  cart: CartLine[],
  rules: AmountGiftRule[],
  foodCents: number,
): GiftOffer[] {
  const offers: GiftOffer[] = []
  for (const raw of rules) {
    const rule = amountRuleOf(raw)
    if (rule === null || foodCents < rule.minCents) continue
    if (giftsInCartFor(cart, rule.id, 'amount') > 0) continue
    offers.push({ ruleId: rule.id, ruleType: 'amount', giftName: rule.giftName, giftPrice: 0, count: 1 })
  }
  return offers
}

/**
 * The cart without the amount gifts it no longer earns - *"if the order drops below, the gift is removed"*.
 *
 * **`null` rules means the read has not answered, and then the cart is returned untouched** - the same array, so a
 * caller can tell nothing changed. A failed read is not a rule switched off.
 */
export function withoutUnearnedAmountGifts<Line extends CartLine>(
  cart: Line[],
  rules: AmountGiftRule[] | null,
  foodCents: number,
): Line[] {
  if (rules === null) return cart
  const reached = new Set(
    rules
      .map(amountRuleOf)
      .filter((rule): rule is NonNullable<typeof rule> => rule !== null && foodCents >= rule.minCents)
      .map((rule) => rule.id),
  )
  const kept = cart.filter(
    (line) => line.giftRuleType !== 'amount' || (line.giftRuleId !== undefined && reached.has(line.giftRuleId)),
  )
  return kept.length === cart.length ? cart : kept
}

/** The cart lines that accepting an offer adds. */
export function giftLinesFor(offer: GiftOffer, cart: CartLine[]): CartLine[] {
  const alreadyGiven = giftsInCartFor(cart, offer.ruleId, offer.ruleType)
  const toAdd = Math.min(offer.count, MAX_GIFTS_PER_OFFER)

  return Array.from({ length: toAdd }, (_unused, index) => ({
    productId: syntheticGiftId(offer.ruleId, alreadyGiven + index + 1),
    productName: offer.giftName,
    unitPrice: offer.giftPrice,
    quantity: 1,
    giftRuleId: offer.ruleId,
    giftRuleType: offer.ruleType,
  }))
}
